// routes/ReferencePicker.jsx — 넘김과 반환이 **같은 방식으로** 근거를 고르는 화면 조각.
//
// 고치기 전의 두 화면:
//   · 넘김 — 체크박스 목록. 다만 찾을 방법이 스크롤뿐이고, 순서를 바꾸려면 풀었다 다시 골라야 했다.
//   · 반환 — `<select multiple>`. 몇 개 골랐는지 보이지 않고, Ctrl 없이 한 번 클릭하면
//     앞서 고른 것이 전부 사라진다. 그 사고는 조용히 난다.
//
// 규칙은 `reference-picker.js` 에 순수 함수로 있고 여기서는 그리기만 한다. 그래서
// 「필터를 좁혀도 선택은 남는다」는 브라우저 없이도 값으로 확인된다.
//
// 이 조각이 지키는 것 하나 더: **고른 것은 언제나 보인다.** 필터를 좁혀 목록에서 사라져도
// 위쪽 「고른 순서」에 남는다. 안 보이는 선택은 사람이 지운 줄 아는 선택이다.

import { Button, Chip, Icon, Input, Select } from '../ui/primitives.jsx';
import {
  filterReferenceChoices, groupOptions, selectedChoices, selectionCounts,
} from './reference-picker.js';

export function ReferencePicker({
  idPrefix,
  legend,
  hint = null,
  choices = [],
  selectedIds = [],
  onToggle,
  onMove = null,
  renderSelected = null,
  groupLabels = {},
  emptyLabel = '고를 수 있는 근거가 없습니다.',
  query = '',
  group = 'all',
  onFilter = null,
}) {
  const groups = groupOptions(choices, groupLabels);
  const visible = filterReferenceChoices(choices, { query, group });
  const counts = selectionCounts({ total: choices.length, visible, selectedIds });
  const chosen = selectedChoices(choices, selectedIds);
  const filtering = Boolean(onFilter) && (String(query).trim() !== '' || group !== 'all');

  return (
    <fieldset class="refpick">
      <legend class="t-body-strong">{legend}</legend>
      {hint ? <p class="t-meta">{hint}</p> : null}

      {choices.length === 0 ? <p class="t-meta">{emptyLabel}</p> : <>
        {onFilter ? (
          <div class="refpick__tools">
            <label class="visually-hidden" for={`${idPrefix}-query`}>근거 좁혀 찾기</label>
            <Input
              id={`${idPrefix}-query`}
              size="sm"
              type="search"
              placeholder="이름·출처로 좁히기"
              value={query}
              onInput={(event) => onFilter({ query: event.currentTarget.value, group })}
              // 찾는 칸에서 Enter 는 **아무것도 보내지 않는다.** 이 입력은 폼 안에 있고,
              // 막지 않으면 검색어를 치다 Enter 한 번에 넘김이 제안되거나 반환이 나간다.
              onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
            />
            {groups.length ? <>
              <label class="visually-hidden" for={`${idPrefix}-group`}>어느 묶음에서 고를지</label>
              <Select
                id={`${idPrefix}-group`}
                size="sm"
                value={group}
                onChange={(event) => onFilter({ query, group: event.currentTarget.value })}
              >
                {groups.map((option) => (
                  <option key={option.value} value={option.value}>{option.label} ({option.count})</option>
                ))}
              </Select>
            </> : null}
            {filtering ? (
              <Button size="sm" tone="ghost" onClick={() => onFilter({ query: '', group: 'all' })}>
                조건 지우기
              </Button>
            ) : null}
          </div>
        ) : null}

        {/* 재촉이 아니라 상태다 — polite 로만 말한다(§절대규칙 2). */}
        <p class="t-meta refpick__count" aria-live="polite">
          {counts.filtered ? `${counts.total}개 중 ${counts.visible}개 보임` : `${counts.total}개`}
          {` · ${counts.selected}개 선택됨`}
          {counts.hiddenSelected > 0 ? ` (그중 ${counts.hiddenSelected}개는 지금 조건 밖입니다)` : ''}
        </p>

        {chosen.length ? (
          <ol class="refpick__chosen" aria-label="고른 근거">
            {chosen.map((item, index) => (
              <li key={item.referenceId} class="refpick__chosen-item">
                <div class="refpick__chosen-head">
                  <span class="refpick__order t-meta">{item.position}</span>
                  <span class="t-body refpick__title">
                    {item.title ?? '이 Task에서 더 이상 보이지 않는 근거'}
                  </span>
                  {onMove ? (
                    <span class="refpick__reorder">
                      {/* 순서를 바꾸려고 풀었다 다시 고르게 하지 않는다 — 그 방법은
                          마우스로만 되고, 푸는 순간 필요도·지시가 함께 사라진다. */}
                      <Button
                        size="xs"
                        tone="ghost"
                        disabled={index === 0}
                        aria-label={`${item.title ?? '이 근거'} — ${item.position - 1}번째로 올리기`}
                        onClick={() => onMove(item.referenceId, -1)}
                      >
                        <Icon name="arrow" rotate={270} />
                      </Button>
                      <Button
                        size="xs"
                        tone="ghost"
                        disabled={index === chosen.length - 1}
                        aria-label={`${item.title ?? '이 근거'} — ${item.position + 1}번째로 내리기`}
                        onClick={() => onMove(item.referenceId, 1)}
                      >
                        <Icon name="arrow" rotate={90} />
                      </Button>
                    </span>
                  ) : null}
                  <Button
                    size="xs"
                    tone="ghost"
                    aria-label={`${item.title ?? '이 근거'} 고르기 해제`}
                    onClick={() => onToggle(item.referenceId)}
                  >
                    해제
                  </Button>
                </div>
                {item.meta ? <p class="t-meta refpick__meta">{item.meta}</p> : null}
                {renderSelected ? renderSelected(item.referenceId) : null}
              </li>
            ))}
          </ol>
        ) : null}

        {visible.length ? (
          <ul class="refpick__list">
            {visible.map((choice) => {
              const picked = selectedIds.includes(choice.referenceId);
              const inputId = `${idPrefix}-pick-${choice.referenceId}`;
              return (
                <li key={choice.referenceId} class="refpick__item">
                  <label class="refpick__row" for={inputId}>
                    <input
                      id={inputId}
                      type="checkbox"
                      checked={picked}
                      onChange={() => onToggle(choice.referenceId)}
                    />
                    <span class="refpick__row-text">
                      <span class="t-body refpick__title">{choice.title}</span>
                      {choice.meta ? <span class="t-meta refpick__meta">{choice.meta}</span> : null}
                    </span>
                    {choice.groupLabel ? <Chip>{choice.groupLabel}</Chip> : null}
                  </label>
                </li>
              );
            })}
          </ul>
        ) : (
          <p class="t-meta">
            조건에 맞는 근거가 없습니다. 고른 것은 그대로 남아 있습니다.
          </p>
        )}
      </>}
    </fieldset>
  );
}
